import LenderLogo from './LenderLogo'
import ComparisonRateDisclaimer from './ComparisonRateDisclaimer'
import { tenantConfig } from '../config/tenant'
import { useRates, type RateRow } from '../hooks/useRates'

// Approx height of one rate row (py-3 + two text lines + the comparison-rate sub-line);
// used to cap a box to ~N rows before it scrolls. Tune on staging if needed.
const ROW_PX = 68

function SkeletonRow() {
  return (
    <li className="py-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0 w-full">
        <div className="w-6 h-6 rounded bg-[#EEEEE8] animate-pulse shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-24 rounded bg-[#EEEEE8] animate-pulse" />
          <div className="h-2.5 w-32 rounded bg-[#F2F2EC] animate-pulse" />
        </div>
      </div>
      <div className="h-4 w-12 rounded bg-[#EEEEE8] animate-pulse shrink-0" />
    </li>
  )
}

function RatesSkeleton() {
  return (
    <div className="space-y-6">
      {[0, 1].map((card) => (
        <div key={card} className="border border-[#E8E8E2] rounded-2xl bg-white p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div className="h-5 w-28 rounded bg-[#EEEEE8] animate-pulse" />
            <div className="h-3 w-24 rounded bg-[#F2F2EC] animate-pulse" />
          </div>
          <ul className="divide-y divide-[#E8E8E2]">
            {[0, 1, 2, 3].map((r) => (
              <SkeletonRow key={r} />
            ))}
          </ul>
        </div>
      ))}
      <span className="sr-only">Loading current rates…</span>
    </div>
  )
}

export default function RatesStrip({ onPickRate }: { onPickRate?: (ratePercent: number) => void }) {
  const { data, error, loading } = useRates()

  if (error) {
    return (
      <div className="text-center text-sm text-gs-text-muted py-6">
        Rates unavailable. Check back soon.
      </div>
    )
  }

  if (loading || !data) {
    return <RatesSkeleton />
  }

  // Both boxes are curated in /dataentry: "Major lenders" = the ★ Major toggle,
  // "Live rates" = the ◉ Carousel picks (the same list feeding the top ticker).
  // Each box hides on its own when nothing is selected; nothing shows if neither.
  const major = data.major ?? []
  const featured = data.carousel ?? []
  if (major.length === 0 && featured.length === 0) return null

  // How many rows a box shows before it scrolls (configured in /dataentry, default 5).
  const cap = data.visible_count ?? 5

  const asAt = (
    <div className="text-xs text-gs-text-muted">
      as at {data.as_at}
      {data.is_stale && <span className="ml-2 text-amber-600">(refresh due)</span>}
    </div>
  )

  const disclaimer = (
    <p className="text-[11px] text-gs-text-muted mt-4 leading-relaxed">
      Indicative only and subject to change. Your rate depends on your full financial
      situation, our brokers will find the right fit for you.
    </p>
  )

  const row = (r: RateRow, keyPrefix: string) => {
    const pct = Number((r.rate * 100).toFixed(2))
    const comp = r.comparison_rate != null ? Number((r.comparison_rate * 100).toFixed(2)) : null
    const key = `${keyPrefix}-${r.lender}-${r.product_name}`
    const content = (
      <>
        <div className="flex items-center gap-3 min-w-0">
          <LenderLogo name={r.lender_name} size={24} />
          <div className="min-w-0">
            <div className="font-medium text-sm text-ink truncate">{r.lender_name}</div>
            <div className="text-xs text-gs-text-muted truncate">{r.product_name}</div>
          </div>
        </div>
        <div className="text-right shrink-0 leading-tight">
          <div className="font-heading font-semibold text-ink tab-num">{pct.toFixed(2)}%</div>
          {comp != null && (
            <div className="text-[10px] text-gs-text-muted tab-num mt-0.5">{comp.toFixed(2)}% comp.</div>
          )}
        </div>
      </>
    )
    // Clickable on the calculator page (native <button> = free keyboard + a11y);
    // plain row everywhere else (e.g. the home hero, no onPickRate).
    if (!onPickRate) {
      return (
        <li key={key} className="py-3 flex items-center justify-between gap-3">
          {content}
        </li>
      )
    }
    return (
      <li key={key} className="py-3">
        <button
          type="button"
          onClick={() => onPickRate(pct)}
          title={`Use ${r.lender_name} rate ${pct}%`}
          aria-label={`Use ${r.lender_name} ${r.product_name} rate ${pct}%`}
          className="w-full text-left flex items-center justify-between gap-3 -mx-2 px-2 py-0 rounded-lg cursor-pointer hover:bg-[#F4FBD9] focus-visible:bg-[#F4FBD9] focus:outline-none transition-colors"
        >
          {content}
        </button>
      </li>
    )
  }

  const box = (testid: string, title: string, rows: RateRow[], keyPrefix: string) => (
    <div data-testid={testid} className="border border-[#E8E8E2] rounded-2xl bg-white p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-heading font-semibold text-ink text-lg">{title}</h3>
        {asAt}
      </div>
      {onPickRate && (
        <div
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
          style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
        >
          <span aria-hidden="true">👆</span> Tap a lender to use its rate in the calculator
        </div>
      )}
      <ul
        className={`divide-y divide-[#E8E8E2]${rows.length > cap ? ' overflow-y-auto rate-scroll pr-1' : ''}`}
        style={rows.length > cap ? { maxHeight: cap * ROW_PX } : undefined}
      >
        {rows.map((r) => row(r, keyPrefix))}
      </ul>
      {disclaimer}
    </div>
  )

  return (
    <div className="space-y-6">
      {major.length > 0 && box('major-lenders-strip', 'Major lenders', major, 'major')}
      {featured.length > 0 && box('live-rates-strip', 'Live rates', featured, 'live')}
      <ComparisonRateDisclaimer />
    </div>
  )
}
